import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { http, USER_KEY } from '../../api/client.js';

const APP_NAMES = ['platform', 'crm', 'operations', 'agent', 'mcp'] as const;
type AppName = (typeof APP_NAMES)[number];
type Environment = 'legacy' | 'crest';

interface RolloutApp {
  rolloutAppId: string;
  appName: AppName;
  targetReleaseId: string;
  enabled: boolean;
  dataReady: boolean;
  readinessNote: string | null;
}

interface Assignment {
  assignmentId: string;
  subjectType: 'user' | 'business_unit' | 'tenant';
  subjectId: string;
  appName: AppName | 'all';
  targetEnvironment: Environment;
  priority: number;
  reason: string | null;
  createdAt: string;
}

interface Rollout {
  rolloutId: string;
  name: string;
  description: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed';
  defaultEnvironment: Environment;
  decisionTtlSeconds: number;
  startsAt: string | null;
  endsAt: string | null;
  emergencyDisabled: boolean;
  version: string;
  apps: RolloutApp[];
  assignments?: Assignment[];
  assignmentCount?: number;
  modifiedAt: string;
}

interface UserOption {
  systemuserid: string;
  fullname?: string | null;
  domainname?: string | null;
}

interface BuNode {
  key: string;
  title: string;
  children?: BuNode[];
}

interface AppDraft {
  appName: AppName;
  targetReleaseId: string;
  enabled: boolean;
  dataReady: boolean;
  readinessNote?: string;
}

const APP_LABELS: Record<AppName, string> = {
  platform: '主应用',
  crm: 'CRM',
  operations: '运营中心',
  agent: 'Agent',
  mcp: 'MCP',
};

const STATUS_META = {
  draft: { label: '草稿', color: 'default' },
  active: { label: '灰度中', color: 'processing' },
  paused: { label: '已暂停', color: 'warning' },
  completed: { label: '已完成', color: 'success' },
} as const;

export function ReleaseRolloutPage() {
  const { message } = App.useApp();
  const [rows, setRows] = useState<Rollout[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [selected, setSelected] = useState<Rollout | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [businessUnits, setBusinessUnits] = useState<Array<{ value: string; label: string }>>([]);
  const [appDrafts, setAppDrafts] = useState<AppDraft[]>(emptyAppDrafts());
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [assignmentForm] = Form.useForm();
  const subjectType = Form.useWatch('subjectType', assignmentForm) ?? 'user';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await http.get<{ rows: Rollout[] }>('/admin/release-rollouts');
      setRows(response.data.rows);
      if (selected) {
        const updated = response.data.rows.find((row) => row.rolloutId === selected.rolloutId);
        if (updated) await openDetail(updated);
      }
    } finally {
      setLoading(false);
    }
  }, [selected?.rolloutId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    Promise.all([
      http.get<{ rows: UserOption[] }>('/admin/users', {
        params: { pageSize: 500 },
      }),
      http.get<{ nodes: BuNode[] }>('/admin/users/bu-tree'),
    ])
      .then(([userResponse, buResponse]) => {
        setUsers(userResponse.data.rows);
        setBusinessUnits(flattenBusinessUnits(buResponse.data.nodes));
      })
      .catch(() => {
        setUsers([]);
        setBusinessUnits([]);
      });
  }, []);

  const openDetail = async (rollout: Rollout) => {
    const response = await http.get<Rollout>(`/admin/release-rollouts/${rollout.rolloutId}`);
    setSelected(response.data);
  };

  const openCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      defaultEnvironment: 'legacy',
      decisionTtlSeconds: 300,
    });
    setAppDrafts(emptyAppDrafts());
    setCreateOpen(true);
  };

  const openEdit = () => {
    if (!selected) return;
    editForm.setFieldsValue({
      name: selected.name,
      description: selected.description,
      defaultEnvironment: selected.defaultEnvironment,
      decisionTtlSeconds: selected.decisionTtlSeconds,
    });
    setAppDrafts(
      APP_NAMES.map((appName) => {
        const current = selected.apps.find((app) => app.appName === appName);
        return {
          appName,
          targetReleaseId: current?.targetReleaseId ?? '',
          enabled: current?.enabled ?? false,
          dataReady: current?.dataReady ?? false,
          readinessNote: current?.readinessNote ?? '',
        };
      }),
    );
    setEditOpen(true);
  };

  const saveRollout = async (editing: boolean) => {
    const form = editing ? editForm : createForm;
    const values = await form.validateFields();
    const apps = appDrafts.filter((app) =>
      editing ? Boolean(app.targetReleaseId.trim()) : app.enabled,
    );
    if (!apps.length || apps.some((app) => !app.targetReleaseId.trim())) {
      message.warning('请至少启用一个应用，并填写对应的发布版本标识');
      return;
    }
    setSubmitting(true);
    try {
      if (editing && selected) {
        await http.patch(`/admin/release-rollouts/${selected.rolloutId}`, {
          ...values,
          apps,
        });
        message.success('灰度批次已更新');
        setEditOpen(false);
        await openDetail(selected);
      } else {
        await http.post('/admin/release-rollouts', { ...values, apps });
        message.success('灰度批次已创建');
        setCreateOpen(false);
      }
      await refresh();
    } finally {
      setSubmitting(false);
    }
  };

  const transition = async (action: 'activate' | 'pause' | 'complete' | 'emergency-stop') => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const response = await http.post<Rollout>(
        `/admin/release-rollouts/${selected.rolloutId}/${action}`,
      );
      setSelected(response.data);
      message.success(
        action === 'emergency-stop' ? '已紧急停止，后续决策将全部回旧环境' : '状态已更新',
      );
      await refresh();
    } finally {
      setSubmitting(false);
    }
  };

  const openAssignment = () => {
    assignmentForm.resetFields();
    assignmentForm.setFieldsValue({
      subjectType: 'user',
      appName: 'all',
      targetEnvironment: 'crest',
      priority: 100,
    });
    setAssignmentOpen(true);
  };

  const saveAssignment = async () => {
    if (!selected) return;
    const values = await assignmentForm.validateFields();
    const subjectId = values.subjectType === 'tenant' ? currentTenantId() : values.subjectId;
    if (!subjectId) {
      message.error('无法识别当前租户，请重新登录');
      return;
    }
    setSubmitting(true);
    try {
      await http.post(`/admin/release-rollouts/${selected.rolloutId}/assignments`, {
        ...values,
        subjectId,
      });
      message.success(
        values.targetEnvironment === 'crest' ? '已加入新环境灰度' : '已配置退出灰度并回旧环境',
      );
      setAssignmentOpen(false);
      await openDetail(selected);
      await refresh();
    } finally {
      setSubmitting(false);
    }
  };

  const removeAssignment = async (assignmentId: string) => {
    if (!selected) return;
    await http.delete(`/admin/release-rollouts/${selected.rolloutId}/assignments/${assignmentId}`);
    message.success('规则已删除；用户将重新按上级规则计算');
    await openDetail(selected);
    await refresh();
  };

  const columns = useMemo<ColumnsType<Rollout>>(
    () => [
      {
        title: '灰度批次',
        dataIndex: 'name',
        render: (_, row) => (
          <Space direction="vertical" size={2}>
            <Button type="link" style={{ padding: 0 }} onClick={() => void openDetail(row)}>
              {row.name}
            </Button>
            <Typography.Text type="secondary">版本 {row.version}</Typography.Text>
          </Space>
        ),
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 120,
        render: (status: Rollout['status'], row) => (
          <Space>
            <Tag color={STATUS_META[status].color}>{STATUS_META[status].label}</Tag>
            {row.emergencyDisabled && <Tag color="red">紧急停用</Tag>}
          </Space>
        ),
      },
      {
        title: '应用范围',
        render: (_, row) =>
          row.apps
            .filter((app) => app.enabled)
            .map((app) => (
              <Tag key={app.appName} color={app.dataReady ? 'green' : 'orange'}>
                {APP_LABELS[app.appName]}
                {app.dataReady ? ' 已就绪' : ' 待就绪'}
              </Tag>
            )),
      },
      {
        title: '成员规则',
        dataIndex: 'assignmentCount',
        width: 100,
        render: (value) => `${value ?? 0} 条`,
      },
      {
        title: '最近更新',
        dataIndex: 'modifiedAt',
        width: 180,
        render: formatTime,
      },
    ],
    [],
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
          <div>
            <Typography.Title level={3} style={{ margin: 0 }}>
              灰度发布
            </Typography.Title>
            <Typography.Text type="secondary">
              按用户、组织或租户控制主应用和各子应用进入/退出新环境。未命中规则时默认留在旧环境。
            </Typography.Text>
          </div>
          <Button type="primary" onClick={openCreate}>
            新建灰度批次
          </Button>
        </Space>
      </Card>

      <Alert
        type="info"
        showIcon
        message="路由控制与业务数据迁移分离"
        description="应用只有在数据就绪被明确确认后才能激活；Agent 进入新环境时会强制要求 MCP 同步就绪。"
      />

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="rolloutId"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          locale={{ emptyText: <Empty description="暂无灰度批次" /> }}
        />
      </Card>

      <RolloutFormModal
        title="新建灰度批次"
        open={createOpen}
        form={createForm}
        drafts={appDrafts}
        onDraftsChange={setAppDrafts}
        submitting={submitting}
        onCancel={() => setCreateOpen(false)}
        onSave={() => void saveRollout(false)}
      />
      <RolloutFormModal
        title="编辑灰度批次与数据就绪"
        open={editOpen}
        form={editForm}
        drafts={appDrafts}
        onDraftsChange={setAppDrafts}
        submitting={submitting}
        onCancel={() => setEditOpen(false)}
        onSave={() => void saveRollout(true)}
      />

      <Drawer
        title={selected?.name ?? '灰度批次'}
        width={860}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        extra={
          <Space>
            <Button onClick={openEdit}>编辑/确认就绪</Button>
            <Button type="primary" onClick={openAssignment}>
              配置进入/退出
            </Button>
          </Space>
        }
      >
        {selected && (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_META[selected.status].color}>
                  {STATUS_META[selected.status].label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="默认环境">
                {selected.defaultEnvironment === 'legacy' ? '旧环境' : '新环境'}
              </Descriptions.Item>
              <Descriptions.Item label="路由有效期">
                {selected.decisionTtlSeconds} 秒
              </Descriptions.Item>
              <Descriptions.Item label="策略版本">{selected.version}</Descriptions.Item>
            </Descriptions>
            <Space wrap>
              {selected.status !== 'active' && selected.status !== 'completed' && (
                <Button
                  type="primary"
                  loading={submitting}
                  onClick={() => void transition('activate')}
                >
                  激活灰度
                </Button>
              )}
              {selected.status === 'active' && (
                <Button loading={submitting} onClick={() => void transition('pause')}>
                  暂停
                </Button>
              )}
              {selected.status !== 'completed' && (
                <Button loading={submitting} onClick={() => void transition('complete')}>
                  完成
                </Button>
              )}
              <Popconfirm
                title="将立即让后续路由决策全部回到旧环境，确定继续？"
                onConfirm={() => void transition('emergency-stop')}
              >
                <Button danger loading={submitting}>
                  紧急停止
                </Button>
              </Popconfirm>
            </Space>
            <Typography.Title level={5}>应用发布目标</Typography.Title>
            <Table
              rowKey="appName"
              size="small"
              pagination={false}
              dataSource={selected.apps}
              columns={appColumns}
            />
            <Typography.Title level={5}>进入/退出规则</Typography.Title>
            <Table
              rowKey="assignmentId"
              size="small"
              pagination={false}
              dataSource={selected.assignments ?? []}
              columns={assignmentColumns(removeAssignment, users, businessUnits)}
              locale={{ emptyText: '尚未配置成员规则' }}
            />
          </Space>
        )}
      </Drawer>

      <Modal
        title="配置进入或退出灰度"
        open={assignmentOpen}
        confirmLoading={submitting}
        onCancel={() => setAssignmentOpen(false)}
        onOk={() => void saveAssignment()}
        okText="保存规则"
      >
        <Form form={assignmentForm} layout="vertical">
          <Form.Item name="subjectType" label="配置对象" rules={[{ required: true }]}>
            <Radio.Group
              options={[
                { label: '用户', value: 'user' },
                { label: '组织', value: 'business_unit' },
                { label: '整个租户', value: 'tenant' },
              ]}
            />
          </Form.Item>
          {subjectType !== 'tenant' && (
            <Form.Item
              name="subjectId"
              label={subjectType === 'user' ? '用户' : '组织'}
              rules={[{ required: true, message: '请选择配置对象' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={
                  subjectType === 'user'
                    ? users.map((user) => ({
                        value: user.systemuserid,
                        label: `${user.fullname ?? user.domainname ?? user.systemuserid}${user.domainname ? `（${user.domainname}）` : ''}`,
                      }))
                    : businessUnits
                }
              />
            </Form.Item>
          )}
          <Form.Item name="appName" label="应用范围" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'all', label: '全部应用' },
                ...APP_NAMES.map((name) => ({
                  value: name,
                  label: APP_LABELS[name],
                })),
              ]}
            />
          </Form.Item>
          <Form.Item name="targetEnvironment" label="操作" rules={[{ required: true }]}>
            <Radio.Group
              options={[
                { label: '进入新环境', value: 'crest' },
                { label: '退出灰度，回旧环境', value: 'legacy' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="priority"
            label="同级优先级"
            tooltip="用户规则始终高于组织规则，组织规则始终高于租户规则。"
          >
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label="原因">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}

function RolloutFormModal(props: {
  title: string;
  open: boolean;
  form: ReturnType<typeof Form.useForm>[0];
  drafts: AppDraft[];
  onDraftsChange: (drafts: AppDraft[]) => void;
  submitting: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  const updateDraft = (appName: AppName, patch: Partial<AppDraft>) =>
    props.onDraftsChange(
      props.drafts.map((draft) => (draft.appName === appName ? { ...draft, ...patch } : draft)),
    );
  return (
    <Modal
      title={props.title}
      width={820}
      open={props.open}
      confirmLoading={props.submitting}
      onCancel={props.onCancel}
      onOk={props.onSave}
      okText="保存"
    >
      <Form form={props.form} layout="vertical">
        <Form.Item
          name="name"
          label="批次名称"
          rules={[{ required: true, message: '请输入批次名称' }]}
        >
          <Input maxLength={160} />
        </Form.Item>
        <Form.Item name="description" label="说明">
          <Input.TextArea rows={2} maxLength={1000} />
        </Form.Item>
        <Space size={16} style={{ width: '100%' }} align="start">
          <Form.Item name="defaultEnvironment" label="未命中规则的默认环境">
            <Select
              style={{ width: 220 }}
              options={[
                { value: 'legacy', label: '旧环境（推荐）' },
                { value: 'crest', label: '新环境' },
              ]}
            />
          </Form.Item>
          <Form.Item name="decisionTtlSeconds" label="路由决策有效期（秒）">
            <InputNumber min={30} max={3600} />
          </Form.Item>
        </Space>
        <Typography.Title level={5}>应用发布目标</Typography.Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          {props.drafts.map((draft) => (
            <Card key={draft.appName} size="small">
              <Space wrap style={{ width: '100%' }}>
                <Switch
                  checked={draft.enabled}
                  onChange={(enabled) => updateDraft(draft.appName, { enabled })}
                />
                <Typography.Text strong style={{ width: 80 }}>
                  {APP_LABELS[draft.appName]}
                </Typography.Text>
                <Input
                  disabled={!draft.enabled}
                  value={draft.targetReleaseId}
                  placeholder="完整 commit SHA 或发布包版本"
                  style={{ width: 280 }}
                  onChange={(event) =>
                    updateDraft(draft.appName, {
                      targetReleaseId: event.target.value,
                    })
                  }
                />
                <Checkbox
                  disabled={!draft.enabled}
                  checked={draft.dataReady}
                  onChange={(event) =>
                    updateDraft(draft.appName, {
                      dataReady: event.target.checked,
                    })
                  }
                >
                  数据已就绪
                </Checkbox>
                <Input
                  disabled={!draft.enabled}
                  value={draft.readinessNote}
                  placeholder="就绪说明/验证单号"
                  style={{ width: 220 }}
                  onChange={(event) =>
                    updateDraft(draft.appName, {
                      readinessNote: event.target.value,
                    })
                  }
                />
              </Space>
            </Card>
          ))}
        </Space>
      </Form>
    </Modal>
  );
}

const appColumns: ColumnsType<RolloutApp> = [
  {
    title: '应用',
    dataIndex: 'appName',
    render: (value: AppName) => APP_LABELS[value],
  },
  { title: '目标版本', dataIndex: 'targetReleaseId' },
  {
    title: '数据门禁',
    dataIndex: 'dataReady',
    render: (value: boolean) => (
      <Tag color={value ? 'green' : 'orange'}>{value ? '已就绪' : '待确认'}</Tag>
    ),
  },
  {
    title: '说明',
    dataIndex: 'readinessNote',
    render: (value) => value || '—',
  },
];

function assignmentColumns(
  remove: (id: string) => Promise<void>,
  users: UserOption[],
  businessUnits: Array<{ value: string; label: string }>,
): ColumnsType<Assignment> {
  const labels = new Map([
    ...users.map(
      (user) => [user.systemuserid, user.fullname ?? user.domainname ?? user.systemuserid] as const,
    ),
    ...businessUnits.map((item) => [item.value, item.label] as const),
  ]);
  return [
    {
      title: '对象',
      render: (_, row) =>
        `${row.subjectType === 'user' ? '用户' : row.subjectType === 'business_unit' ? '组织' : '租户'}：${labels.get(row.subjectId) ?? row.subjectId}`,
    },
    {
      title: '应用',
      dataIndex: 'appName',
      render: (value: AppName | 'all') => (value === 'all' ? '全部应用' : APP_LABELS[value]),
    },
    {
      title: '路由',
      dataIndex: 'targetEnvironment',
      render: (value: Environment) => (
        <Tag color={value === 'crest' ? 'blue' : 'default'}>
          {value === 'crest' ? '进入新环境' : '退出到旧环境'}
        </Tag>
      ),
    },
    { title: '原因', dataIndex: 'reason', render: (value) => value || '—' },
    {
      title: '操作',
      width: 90,
      render: (_, row) => (
        <Popconfirm
          title="删除后会重新按上级规则计算，确定？"
          onConfirm={() => void remove(row.assignmentId)}
        >
          <Button type="link" danger>
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];
}

function emptyAppDrafts(): AppDraft[] {
  return APP_NAMES.map((appName) => ({
    appName,
    targetReleaseId: '',
    enabled: false,
    dataReady: false,
  }));
}

function flattenBusinessUnits(nodes: BuNode[], depth = 0): Array<{ value: string; label: string }> {
  return nodes.flatMap((node) => [
    { value: node.key, label: `${'　'.repeat(depth)}${node.title}` },
    ...flattenBusinessUnits(node.children ?? [], depth + 1),
  ]);
}

function currentTenantId(): string | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw).tenantId ?? null) : null;
  } catch {
    return null;
  }
}

function formatTime(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '—';
}
